// ─────────────────────────────────────────────
//  Toast Context — accessible, app-wide feedback
//  Replaces scattered alert() calls. The container is an
//  aria-live region so screen readers announce updates.
// ─────────────────────────────────────────────
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import tokens from "../theme/tokens";

const { color, space, radius, shadow, font } = tokens;

const ToastContext = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message, type = "info", ttl = 4000) => {
      const id = nextId++;
      setToasts((prev) => [...prev, { id, message, type }]);
      if (ttl) setTimeout(() => dismiss(id), ttl);
      return id;
    },
    [dismiss]
  );

  // Stable identity: consumers list `toast` in useEffect deps. Without memo,
  // every toast push re-rendered the provider, minted a new object, and forced
  // every such effect to tear down and resubscribe — an error toast inside an
  // onSnapshot error handler then looped forever (toast → re-render → new
  // identity → resubscribe → error → toast…).
  const toast = useMemo(
    () => ({
      info: (m) => push(m, "info"),
      success: (m) => push(m, "success"),
      warning: (m) => push(m, "warning", 6000),
      error: (m) => push(m, "error", 6000),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div style={styles.container} aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div key={t.id} role="status" style={{ ...styles.toast, ...styles[t.type] }}>
            <span>{t.message}</span>
            <button
              style={styles.close}
              aria-label="Dismiss notification"
              onClick={() => dismiss(t.id)}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// Falls back to a no-throw stub if used outside the provider (e.g. in tests).
export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (ctx) return ctx;
  const noop = (m) => { if (typeof console !== "undefined") console.log("[toast]", m); };
  return { info: noop, success: noop, warning: noop, error: noop };
};

// Light-palette restyle (D-08). API + a11y markup above are unchanged —
// only these style values were re-themed onto tokens.
const styles = {
  container: {
    position: "fixed",
    bottom: 20,
    right: 20,
    display: "flex",
    flexDirection: "column",
    gap: space.sm,
    zIndex: 1000,
    maxWidth: "92vw",
  },
  toast: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space.md,
    minWidth: 260,
    maxWidth: 380,
    padding: "12px 16px",
    borderRadius: radius.md,
    fontSize: font.size.body,
    fontFamily: font.family,
    color: color.text.primary,
    background: color.surface.card,
    border: "1px solid " + color.border.default,
    boxShadow: shadow.lg,
  },
  info: { borderColor: color.accent.primary, background: "#ffffff" },
  success: { borderColor: color.accent.active, background: "#f0fdf8" },
  warning: { borderColor: "#e0b84c", background: "#fff4d6" },
  error: { borderColor: color.accent.error, background: "#fff5f5" },
  close: {
    background: "transparent",
    border: "none",
    color: color.text.secondary,
    cursor: "pointer",
    fontSize: font.size.body,
    flexShrink: 0,
  },
};
